<script setup lang="ts">
import { computed, defineAsyncComponent, nextTick, ref, watch } from 'vue';
import { galleryImageSrcset, type GalleryPhoto } from '~/utils/gallery';

const GalleryViewer = defineAsyncComponent(() => import('./GalleryViewer.vue'));
const props = defineProps<{ photos: GalleryPhoto[]; name: string; offset?: number }>();
const { t } = useI18n();
const active = ref<number | null>(null);
let opener: HTMLElement | null = null;
const label = (photo: GalleryPhoto, index: number) => photo.name || t('gallery_module.photo_name', { album: props.name, number: (props.offset || 0) + index + 1 });
function open(index: number, event: MouseEvent) {
  opener = event.currentTarget as HTMLElement;
  active.value = index;
}
async function close() {
  active.value = null;
  await nextTick();
  opener?.focus();
}
watch(() => props.photos, () => { active.value = null; });
const viewerPhotos = computed(() => props.photos.map((photo, index) => ({ ...photo, name: label(photo, index) })));
</script>

<template>
  <div>
    <div class="portfolio-grid">
      <figure v-for="(photo, index) in photos" :key="photo.id" class="portfolio-photo">
        <a :href="photo.photo" :aria-label="t('gallery_module.open_photo', { name: label(photo, index) })" @click.prevent="open(index, $event)">
          <img :src="photo.thumb || photo.photo" :srcset="galleryImageSrcset(photo)" sizes="(min-width: 992px) 30vw, (min-width: 576px) 46vw, 94vw"
            :alt="label(photo, index)" :width="photo.width || undefined" :height="photo.height || undefined"
            :loading="index < 3 ? 'eager' : 'lazy'" :fetchpriority="index === 0 ? 'high' : 'auto'" decoding="async" />
          <span class="portfolio-zoom" aria-hidden="true"><Icon name="lucide:expand" size="22" /></span>
        </a>
        <figcaption v-if="photo.name">{{ photo.name }}</figcaption>
      </figure>
    </div>
    <ClientOnly>
      <GalleryViewer v-if="active !== null" :photos="viewerPhotos" :initial-index="active" :name="name" @close="close" />
    </ClientOnly>
  </div>
</template>

<style scoped>
.portfolio-grid { columns: 3; column-gap: 1.5rem; }
.portfolio-photo { margin: 0 0 1.5rem; break-inside: avoid; }
.portfolio-photo a { display: block; position: relative; overflow: hidden; border-radius: 14px; background: var(--el-fill-color-light); }
.portfolio-photo a:focus-visible { outline: 3px solid var(--el-color-primary); outline-offset: 4px; }
.portfolio-photo img { display: block; width: 100%; height: auto; transition: transform .4s ease; }
.portfolio-zoom { position: absolute; right: 1rem; bottom: 1rem; display: flex; padding: .65rem; border-radius: 50%; background: rgba(0,0,0,.55); color: #fff; opacity: 0; transition: opacity .2s ease; }
.portfolio-photo a:hover .portfolio-zoom, .portfolio-photo a:focus-visible .portfolio-zoom { opacity: 1; }
.portfolio-photo a:hover img { transform: scale(1.02); }
.portfolio-photo figcaption { margin-top: .5rem; color: var(--el-text-color-secondary); font-size: var(--fs--1); }
@media (max-width: 991px) { .portfolio-grid { columns: 2; } }
@media (max-width: 575px) { .portfolio-grid { columns: 1; } .portfolio-zoom { opacity: 1; } }
@media (prefers-reduced-motion: reduce) { .portfolio-photo img, .portfolio-zoom { transition: none; } }
</style>
